import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import SearchBox from '@/components/feature/SearchBox';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { plotEpisodes } from '@/mocks/plotEpisodes';
import { castMembers } from '@/mocks/castMembers';
import { newsArticles } from '@/mocks/newsArticles';
import { episodeNo, formatDate } from '@/utils/format';

const tabs = [
  { key: 'all', label: '全部' },
  { key: 'plot', label: '剧情' },
  { key: 'cast', label: '演员' },
  { key: 'news', label: '动态' },
];

export default function SearchResults() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState('all');
  const keyword = (params.get('q') ?? '').trim();
  const q = keyword.toLowerCase();

  usePageMeta({
    title: keyword ? `搜索「${keyword}」 - 禁止的爱善良的小姨子专题` : '站内搜索 - 禁止的爱善良的小姨子专题',
    description: keyword
      ? `在禁止的爱善良的小姨子专题站内搜索「${keyword}」，查看匹配的剧情分集、演员资料与最新动态。`
      : '搜索禁止的爱善良的小姨子的剧情、演员与动态内容。',
    keywords: '站内搜索,禁止的爱善良的小姨子,剧情搜索,演员搜索',
  });

  const matchedPlots = useMemo(
    () =>
      q
        ? plotEpisodes.filter((item) =>
            `${item.title}${item.summary}${item.paragraphs.join('')}第${item.episode}集`.toLowerCase().includes(q),
          )
        : [],
    [q],
  );

  const matchedCast = useMemo(
    () =>
      q
        ? castMembers.filter((item) =>
            `${item.name}${item.role}${item.tagline}${item.bio}`.toLowerCase().includes(q),
          )
        : [],
    [q],
  );

  const matchedNews = useMemo(
    () =>
      q
        ? newsArticles.filter((item) =>
            `${item.title}${item.summary}${item.paragraphs.join('')}${item.tags.join('')}`.toLowerCase().includes(q),
          )
        : [],
    [q],
  );

  const total = matchedPlots.length + matchedCast.length + matchedNews.length;
  const counts: Record<string, number> = {
    all: total,
    plot: matchedPlots.length,
    cast: matchedCast.length,
    news: matchedNews.length,
  };

  const showPlots = tab === 'all' || tab === 'plot';
  const showCast = tab === 'all' || tab === 'cast';
  const showNews = tab === 'all' || tab === 'news';

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb items={[{ label: '搜索' }]} />
          <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs tracking-[0.24em] text-primary-600">SITE SEARCH</p>
              <h1 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">
                {keyword ? `搜索「${keyword}」` : '搜索站内内容'}
              </h1>
              <p className="mt-2 text-sm text-foreground-500">
                {keyword ? `共找到 ${total} 条相关内容，覆盖剧情分集、演员资料与最新动态。` : '输入关键词，搜索剧情、演员或动态内容。'}
              </p>
            </div>
            <div className="w-full lg:max-w-sm">
              <SearchBox />
            </div>
          </div>
        </div>
      </div>

      <section className="bg-background-50">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
          <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1 sm:w-fit">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${
                  tab === item.key
                    ? 'bg-primary-600 text-background-50'
                    : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                {item.label}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                    tab === item.key ? 'bg-background-50/20 text-background-50' : 'bg-background-200 text-foreground-500'
                  }`}
                >
                  {counts[item.key]}
                </span>
              </button>
            ))}
          </div>

          {!keyword ? (
            <div className="mt-6">
              <EmptyState
                title="还没有输入搜索内容"
                description="试试搜索「林小满」「雨夜」「幕后」等关键词，或在顶部搜索框输入你想找的内容。"
                actionLabel="返回首页"
              />
            </div>
          ) : total === 0 ? (
            <div className="mt-6">
              <EmptyState
                title="没有找到相关内容"
                description={`暂时没有与「${keyword}」匹配的剧情、演员或动态。可以换一个关键词，或浏览分类页面。`}
                actionLabel="返回首页"
              />
            </div>
          ) : (
            <div className="mt-6 space-y-10">
              {showPlots && matchedPlots.length > 0 && (
                <div>
                  <div className="flex items-center justify-between border-b border-background-200 pb-3">
                    <h2 className="flex items-center gap-2 font-heading text-base text-foreground-900">
                      <span className="h-4 w-[3px] rounded-full bg-primary-600" />
                      剧情分集
                      <span className="text-xs text-foreground-400">{matchedPlots.length}</span>
                    </h2>
                    <Link to="/category/plot" className="text-xs text-foreground-500 hover:text-primary-600">
                      全部分集
                    </Link>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {matchedPlots.map((item) => (
                      <Link
                        key={item.id}
                        to={`/detail/plot/${item.id}`}
                        className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                      >
                        <div className="h-[68px] w-[96px] shrink-0 overflow-hidden rounded-md bg-background-200">
                          <img
                            src={item.image}
                            alt={`${episodeNo(item.episode)} ${item.title} 剧照`}
                            title={`${item.title} - 禁止的爱善良的小姨子剧情`}
                            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] text-primary-600">{episodeNo(item.episode)}</p>
                          <h3 className="mt-1 line-clamp-1 font-heading text-sm text-foreground-900 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </h3>
                          <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{item.summary}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {showCast && matchedCast.length > 0 && (
                <div>
                  <div className="flex items-center justify-between border-b border-background-200 pb-3">
                    <h2 className="flex items-center gap-2 font-heading text-base text-foreground-900">
                      <span className="h-4 w-[3px] rounded-full bg-primary-600" />
                      演员资料
                      <span className="text-xs text-foreground-400">{matchedCast.length}</span>
                    </h2>
                    <Link to="/category/cast" className="text-xs text-foreground-500 hover:text-primary-600">
                      全部演员
                    </Link>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                    {matchedCast.map((member) => (
                      <Link
                        key={member.id}
                        to={`/detail/cast/${member.id}`}
                        className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                      >
                        <div className="h-[180px] w-full overflow-hidden bg-background-200">
                          <img
                            src={member.image}
                            alt={`演员${member.name}饰演${member.role}`}
                            title={`${member.name} - 禁止的爱善良的小姨子演员`}
                            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                          />
                        </div>
                        <div className="p-3">
                          <p className="font-heading text-sm text-foreground-900 transition-colors group-hover:text-primary-600">
                            {member.name}
                          </p>
                          <p className="mt-1 text-xs text-foreground-500">饰 {member.role}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {showNews && matchedNews.length > 0 && (
                <div>
                  <div className="flex items-center justify-between border-b border-background-200 pb-3">
                    <h2 className="flex items-center gap-2 font-heading text-base text-foreground-900">
                      <span className="h-4 w-[3px] rounded-full bg-primary-600" />
                      最新动态
                      <span className="text-xs text-foreground-400">{matchedNews.length}</span>
                    </h2>
                    <Link to="/category/news" className="text-xs text-foreground-500 hover:text-primary-600">
                      全部动态
                    </Link>
                  </div>
                  <div className="mt-4 divide-y divide-background-200">
                    {matchedNews.map((item) => (
                      <Link
                        key={item.id}
                        to={`/detail/news/${item.id}`}
                        className="group flex items-start gap-4 py-4"
                      >
                        <div className="h-[64px] w-[92px] shrink-0 overflow-hidden rounded-md bg-background-200">
                          <img
                            src={item.image}
                            alt={item.title}
                            title={`${item.title} - 禁止的爱善良的小姨子动态`}
                            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-[11px] text-foreground-400">
                            <span className="text-primary-500">{item.category}</span>
                            <span>{formatDate(item.date)}</span>
                          </div>
                          <h3 className="mt-1 line-clamp-2 font-heading text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </h3>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <p className="mt-10 text-center text-[11px] text-foreground-400">
            搜索为前端演示匹配，不连接真实后台索引
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}